import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Typography,
    Paper,
    CircularProgress,
    TextField,
    Button,
    Divider
} from '@material-ui/core';
import MaterialTable from 'material-table';
import { format } from 'date-fns';
import { apiATC } from '../../config';

function AnalyseMetal() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dateDebut, setDateDebut] = useState(
        format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd')
    );
    const [dateFin, setDateFin] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [selectedDetail, setSelectedDetail] = useState(null);

    // Inputs utilisateur
    const [achat, setAchat] = useState(1150);
    const [variation, setVariation] = useState(-1166);
    const [variationColl, setVariationColl] = useState(138.6); 
    const [confieClient, setConfieClient] = useState(450);
    const [clal, setClal] = useState(-344.73);
    const [clalYear1, setClalYear1] = useState(121.49);

    const fetchData = (dDebut, dFin) => {
        setLoading(true);
        axios
            .get(`${apiATC}/analyse/analyse_poids.php?date_d=${dDebut}&date_f=${dFin}`)
            .then((res) => {
                setData(res.data);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchData(dateDebut, dateFin);
    }, [dateDebut, dateFin]);

    const handleApplyFilter = () => fetchData(dateDebut, dateFin);

    if (loading) return <div style={{ padding: 20 }}><CircularProgress /></div>;
    if (!data) return <Typography>Erreur lors du chargement des données.</Typography>;

    // Valeurs numériques
    const achatVal = Number(achat) || 0;
    const variationVal = Number(variation) || 0;
    const variationCollVal = Number(variationColl) || 0;
    const confieVal = Number(confieClient) || 0;
    const clalVal = Number(clal) || 0;
    const clalYear1Val = Number(clalYear1) || 0;

    const orDestocke = Number(data?.totaux_globaux?.poids_sorti_total) || 0;
    const orEntreeStock = Number(data?.totaux_globaux?.poids_entree_total) || 0;
    const orLivre = Number(data?.totaux_globaux?.poids_livre_total) || 0;
    const orStockLivre = Number(data?.totaux_globaux?.poids_stock_livre_total) || 0;

    const PERTE = 0.997;

    // Conversion
    const achatOr750 = achatVal / 0.75;
    const confieOr750 = confieVal / 0.75;
    const clalOr750 = clalVal / 0.75;
    const clalYear1Or750 = clalYear1Val / 0.75;

    const variationApplique = variationVal * PERTE;
    const orDestockeApplique = orDestocke * PERTE;

    // Calculs solde
    const entreesTotal = (achatOr750 - variationApplique + orDestockeApplique + variationCollVal).toFixed(2); // variationColl déjà en 750
    const sortiesTotal = (orEntreeStock + orLivre).toFixed(2); // sans Confié Client
    const solde1 = (entreesTotal - sortiesTotal).toFixed(2);
    const solde2 = confieOr750.toFixed(2);
    const solde3 = (clalOr750 - clalYear1Or750).toFixed(2);
    const soldeFinal = (
        parseFloat(solde1) + parseFloat(solde2) + parseFloat(solde3)
    ).toFixed(2);

    // Styles
    const paperStyle = {
        padding: 16,
        textAlign: 'center',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minWidth: 140,
        flex: 1,
        height: 160,
        borderRadius: 12
    };

    const resumeStyle = {
        padding: 20,
        backgroundColor: parseFloat(soldeFinal) >= 0 ? '#4caf50' : '#f44336',
        color: '#fff',
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        flex: '0 0 320px', // max largeur = 2 papers
        minWidth: 280,
        height: '80%', // prend toute la hauteur
        overflowY: 'auto'
    };

    const columnsCommon = [
        { title: 'OF', field: 'of' },
        { title: 'Centre Vide', field: 'ref_centre_vide' },
        { title: 'Description', field: 'description' },
        { title: 'Poids (g)', field: 'poids', type: 'numeric' }
    ];

    const detailsMap = {
        entree: { title: 'Détails Entrée Stock', columns: columnsCommon, data: data.poids_entree_stock.liste_of },
        sortie: { title: 'Détails Or Déstocké', columns: columnsCommon, data: data.poids_sorti_stock.liste_of },
        livre: { title: 'Détails Livré', columns: [...columnsCommon, { title: 'Métal', field: 'metal' }, { title: 'Facture', field: 'id_fact' }], data: data.poids_livre.liste_of },
        pesee: {
            title: 'Pesée Atelier', columns: [
                { title: 'Num Pesée', field: 'num_pesee' },
                { title: 'Date', field: 'date' },
                { title: 'Poids OF Entré', field: 'poids_of_rentre', type: 'numeric' },
                { title: 'Poids OF Sorti', field: 'poids_of_sorti', type: 'numeric' },
                { title: 'Pds Or Entré', field: 'pds_or_e', type: 'numeric' },
                { title: 'Pds Or Sorti', field: 'pds_or_s', type: 'numeric' }
            ], data: data.pesee_atelier.details
        }
    };

    return (
        <div style={{ padding: 20, height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 20, position: 'relative' }}>
                <Typography variant="h4">Analyse Métal</Typography>
                <div style={{ position: 'absolute', right: 0, display: 'flex', gap: 8 }}>
                    <TextField label="Date début" type="date" variant="outlined" size="small" value={dateDebut} onChange={e => setDateDebut(e.target.value)} InputLabelProps={{ shrink: true }} />
                    <TextField label="Date fin" type="date" variant="outlined" size="small" value={dateFin} onChange={e => setDateFin(e.target.value)} InputLabelProps={{ shrink: true }} />
                    <Button variant="contained" color="primary" onClick={handleApplyFilter}>Appliquer</Button>
                </div>
            </div>

            {/* Layout 2 colonnes */}
            <div style={{ display: 'flex', flex: 1, gap: 12, overflow: 'hidden' }}>
                {/* Colonne gauche */}
                <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 12, overflow: 'auto' }}>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <Paper style={paperStyle} onClick={() => setSelectedDetail(null)}>
                            <Typography variant="h6">Achat OR FIN</Typography>
                            <TextField type="number" value={achat} onChange={e => setAchat(e.target.value)} variant="outlined" size="small" style={{ width: '140px', textAlign: 'center', marginTop: 8 }} />
                            <Typography variant="body2" color="textSecondary">{achatOr750.toFixed(2)} g</Typography>
                        </Paper>

                        <Paper style={paperStyle} onClick={() => setSelectedDetail(null)}>
                            <Typography variant="h6">Variation Coffre</Typography>
                            <TextField type="number" value={variation} onChange={e => setVariation(e.target.value)} variant="outlined" size="small" style={{ width: '120px', textAlign: 'center', marginTop: 8 }} />
                            <Typography variant="body2" color="textSecondary">Perte 3‰ : {(Math.abs(variationVal) * (1 - PERTE)).toFixed(2)} g</Typography>
                        </Paper>

                        <Paper style={paperStyle} onClick={() => setSelectedDetail(null)}>
                            <Typography variant="h6">Variation Coll</Typography>
                            <TextField
                                type="number"
                                value={variationColl}
                                onChange={e => setVariationColl(e.target.value)}
                                variant="outlined"
                                size="small"
                                style={{ width: '120px', textAlign: 'center', marginTop: 8 }}
                            />
                            <Typography variant="body2" color="textSecondary">{variationCollVal.toFixed(2)} g</Typography>
                        </Paper>

                        <Paper style={paperStyle} onClick={() => setSelectedDetail('sortie')}>
                            <Typography variant="h6">Or Déstocké</Typography>
                            <Typography variant="h5">{orDestocke} g</Typography>
                        </Paper>

                        <Divider orientation="vertical" flexItem />

                        <Paper style={paperStyle} onClick={() => setSelectedDetail('entree')}>
                            <Typography variant="h6">Or Entrée Stock</Typography>
                            <Typography variant="h5">{orEntreeStock} g</Typography>
                        </Paper>

                        <Paper style={paperStyle} onClick={() => setSelectedDetail('livre')}>
                            <Typography variant="h6">OR Livré</Typography>
                            <Typography variant="h5">{orLivre} g</Typography>
                            <Typography variant="body2" color="textSecondary">Pris du stock : {orStockLivre} g</Typography>
                        </Paper>

                        <Paper style={paperStyle} onClick={() => setSelectedDetail(null)}>
                            <Typography variant="h6">Confié Client</Typography>
                            <TextField type="number" value={confieClient} onChange={e => setConfieClient(e.target.value)} variant="outlined" size="small" style={{ width: '140px', textAlign: 'center', marginTop: 8 }} />
                            <Typography variant="body2" color="textSecondary">{confieOr750.toFixed(2)} g</Typography>
                        </Paper>

                        <Paper style={paperStyle} onClick={() => setSelectedDetail(null)}>
                            <Typography variant="h6">CLAL n-1</Typography>
                            <TextField type="number" value={clalYear1} onChange={e => setClalYear1(e.target.value)} variant="outlined" size="small" style={{ width: '140px', textAlign: 'center', marginTop: 8 }} />
                            <Typography variant="body2" color="textSecondary">{clalYear1Or750.toFixed(2)} g</Typography>
                        </Paper>

                        <Paper style={paperStyle} onClick={() => setSelectedDetail(null)}>
                            <Typography variant="h6">CLAL Fin</Typography>
                            <TextField type="number" value={clal} onChange={e => setClal(e.target.value)} variant="outlined" size="small" style={{ width: '140px', textAlign: 'center', marginTop: 8 }} />
                            <Typography variant="body2" color="textSecondary">{clalOr750.toFixed(2)} g</Typography>
                        </Paper>
                    </div>

                    {/* MaterialTable */}
                    <div style={{ flex: 1, overflow: 'auto' }}>
                        {selectedDetail && (
                            <MaterialTable
                                title={detailsMap[selectedDetail].title}
                                columns={detailsMap[selectedDetail].columns}
                                data={detailsMap[selectedDetail].data}
                                options={{ search: true, paging: true, padding: 'dense', maxBodyHeight: '100%' }}
                            />
                        )}
                    </div>
                </div>

                {/* Colonne droite : résumé */}
                <Paper style={resumeStyle}>
                    <Typography variant="h6" style={{ marginBottom: 12 }}>Résumé Poids 750/1000</Typography>

                    <Typography variant="body1"><strong>Entrée :</strong> {entreesTotal} g</Typography>
                    <Typography variant="body1"><strong>Sortie :</strong> {sortiesTotal} g</Typography>
                    <Typography variant="body1"><strong>Solde :</strong> {solde1} g</Typography>

                    <Divider style={{ margin: '8px 0', backgroundColor: 'rgba(255,255,255,0.5)' }} />

                    <Typography variant="body1"><strong>Confié Client :</strong> {solde2} g</Typography>

                    <Divider style={{ margin: '8px 0', backgroundColor: 'rgba(255,255,255,0.5)' }} />

                    <Typography variant="body2"><strong>CLAL n-1 :</strong> {clalYear1Or750.toFixed(2)} g</Typography>
                    <Typography variant="body2"><strong>CLAL :</strong> {clalOr750.toFixed(2)} g</Typography>
                    <Typography variant="body1"><strong>Solde :</strong> {solde3} g</Typography>

                    <Divider style={{ margin: '8px 0', backgroundColor: 'rgba(255,255,255,0.5)' }} />

                    <Typography variant="h6" style={{ fontWeight: 'bold' }}>Solde Final : {soldeFinal} g</Typography>
                </Paper>
            </div>
        </div>
    );
}

export default AnalyseMetal;
